// What-if analysis for the simulation: the load put into the models (items a year at each input) and the capacity
// of the roles (people) are changed, and the analysis data is adjusted to match, so the same simulation can be run
// on it and compared with today's. Alongside the simulation, the people each role needs to meet the adjusted load are
// worked out from its hours and capacity, so the user sees what the change requires as they make it
import { AnalysisEdge, AnalysisNode } from '../common/analysis-model'
import { getInsightsForNode, GraphData, NODE_SUPERTYPE, NODE_TYPE, ORGANISATION_MODEL_KEY } from '../common/linqset-model'
import { RoleCapacity, WORKING_HOURS_PER_YEAR } from './simulation-report'

// where items are put in: a model's inputs, and the steps that set how often they happen
export interface WhatIfInput {
    key: string
    name: string
    modelKey: string
    model: string
    itemsPerYear: number
}

// a role that performs steps in the models looked at
export interface WhatIfRole {
    key: string
    name: string
    people: number              // 0 when not set, so its capacity can't be changed
    hours: number               // work a year, today
    capacity: number            // hours a year it has for it, today
    cost: number                // a year, today
    actionKeys: string[]        // the steps it performs in the models looked at
}

export interface WhatIfSettings {
    loadFactor: number                          // the overall load, as a multiple of today's (1 is today's)
    inputItems: { [inputKey: string]: number }  // items a year at an input, where set apart from the overall load
    people: { [roleKey: string]: number }       // people on a role, where changed
    detourShares: { [detourKey: string]: number }   // the share (0 to 1) of items taking a detour, where changed
}

export const DEFAULT_WHAT_IF: WhatIfSettings = { loadFactor: 1, inputItems: {}, people: {}, detourShares: {} }

// a detour from the main flow: items leave it at the split and return at the join, and the steps between are
// exception work. The what-if can change the share of items taking it
export interface WhatIfDetour {
    key: string                 // "<split>><join>"
    leavesAt: string
    returnsAt: string
    model: string
    stepKeys: string[]
    steps: string[]
    firstStep: string           // the detour's first step, which the items leaving the split go to
    share: number               // of the items leaving the split, today (0 to 1)
    itemsPerYear: number        // taking the detour, today
    splitItems: number          // leaving the split a year, today
    hours: number               // a year, on the detour's steps, today
    cost: number
}

// what a role needs to meet the adjusted load, from its hours and capacity: the people its work takes at the capacity
// each of them has today, before the simulation says anything about how the work arrives
export interface RoleRequirement {
    key: string
    name: string
    peopleToday: number
    people: number              // as set in the what-if
    hoursToday: number
    hours: number               // the adjusted work a year
    capacity: number            // the adjusted capacity a year
    utilisation: number | null  // hours as a share of capacity; null when people aren't set
    needed: number | null       // people the adjusted work takes; null when people aren't set
    extra: number               // more than the what-if has (negative when it has more than it needs)
    cost: number                // a year, at the what-if's people
}

export interface WhatIfOptions {
    inputs: WhatIfInput[]
    roles: WhatIfRole[]
    detours: WhatIfDetour[]
}

export const detourShare = (detour: WhatIfDetour, settings: WhatIfSettings) =>
    detour.key in settings.detourShares ? Math.max(0, Math.min(1, settings.detourShares[detour.key])) : detour.share

export const inputFactor = (input: WhatIfInput, settings: WhatIfSettings) =>
    input.key in settings.inputItems && input.itemsPerYear > 0 ? settings.inputItems[input.key]/input.itemsPerYear : settings.loadFactor

export const peopleRatio = (role: WhatIfRole, settings: WhatIfSettings) =>
    role.people > 0 && role.key in settings.people ? Math.max(0, settings.people[role.key])/role.people : 1

const isSource = (n: AnalysisNode) => n.superType === NODE_SUPERTYPE.ACTION && (n.isStarting || !!n.setsFrequency)

// the inputs, roles and detours of the models looked at, as the what-if can change them
export const whatIfOptions = (data: { nodes: AnalysisNode[], edges?: AnalysisEdge[] }, modelKeys: Set<string>, organisationModel: GraphData | undefined, workspaceKey: string,
    modelNames: { [key: string]: string }): WhatIfOptions => {
    const nodes = data.nodes.filter(n => modelKeys.has(n.modelKey))
    const names = new Map<string, string>(nodes.map(n => [n._key, n.name || 'Unnamed step']))
    ;(data.edges || []).forEach(e => { if (e.informationKey && e.informationName && !names.has(e.informationKey)) names.set(e.informationKey, e.informationName) })
    const inputs: WhatIfInput[] = nodes.filter(isSource)
        .map(n => ({ key: n._key, name: n.name || 'Unnamed step', modelKey: n.modelKey, model: modelNames[n.modelKey] || '', itemsPerYear: n.items || 0 }))
        .sort((a, b) => b.itemsPerYear - a.itemsPerYear || a.name.localeCompare(b.name))

    const actionsByRole = new Map<string, string[]>()
    nodes.filter(n => n.superType === NODE_SUPERTYPE.ACTION).forEach(n => (n.roles || []).forEach(r => (actionsByRole.get(r._key) || actionsByRole.set(r._key, []).get(r._key)!).push(n._key)))
    const roles: WhatIfRole[] = (organisationModel?.nodes || [])
        .filter(n => n.superType === NODE_SUPERTYPE.ROLE && n.type !== NODE_TYPE.EXTERNAL_ORGANISATION && actionsByRole.has(n._key))
        .map(n => {
            const i = getInsightsForNode(n, ORGANISATION_MODEL_KEY, workspaceKey)
            const a = i?.costAllocation
            return {
                key: n._key, name: n.name || 'Unnamed role', people: n.properties?.people || i?.numPeople || 0, hours: i?.hours || 0, capacity: i?.modelledCapacity || 0,
                cost: a ? (a.allocated || 0) + (a.unallocated || 0) + (a.unmodelled || 0) : 0, actionKeys: actionsByRole.get(n._key)!,
            }
        })
        .sort((a, b) => (b.capacity ? b.hours/b.capacity : 0) - (a.capacity ? a.hours/a.capacity : 0) || b.hours - a.hours)

    // the detours, each the steps on it between where items leave the main flow and where they return
    const groups = new Map<string, AnalysisNode[]>()
    nodes.filter(n => n.superType === NODE_SUPERTYPE.ACTION && n.onDetour && n.detourSplitKey && n.detourJoinKey)
        .forEach(n => { const key = `${n.detourSplitKey}>${n.detourJoinKey}`; (groups.get(key) || groups.set(key, []).get(key)!).push(n) })
    const edges = data.edges || []
    const detours: WhatIfDetour[] = [...groups.entries()].map(([key, steps]) => {
        const share = Math.max(...steps.map(s => s.detourShare || 0), 0)
        const itemsPerYear = Math.max(...steps.map(s => s.items || 0), 0)
        // the first step is the one the split sends items to: from the split itself, or along the split's information
        const split = steps[0].detourSplitKey!
        const first = steps.find(s => edges.some(e => e.to === s._key && (e.from === split || e.informationKey === split))) || steps[0]
        return {
            key, leavesAt: names.get(steps[0].detourSplitKey!) || 'an earlier step', returnsAt: names.get(steps[0].detourJoinKey!) || 'a later step',
            model: modelNames[steps[0].modelKey] || '', stepKeys: steps.map(s => s._key), steps: steps.map(s => s.name || 'Unnamed step'), firstStep: first.name || 'Unnamed step',
            share, itemsPerYear, splitItems: share > 0 ? itemsPerYear/share : 0,
            hours: steps.reduce((sum, s) => sum + (s.hours || 0), 0), cost: steps.reduce((sum, s) => sum + (s.cost || 0), 0),
        }
    }).sort((a, b) => b.cost - a.cost || b.hours - a.hours)
    return { inputs, roles, detours }
}

// the steps on the main flow between a detour's split and join: on a path from the split to the join, not on the
// detour. A split that is information rather than a step isn't in the analysis graph, so the flow starts from the
// steps that information goes to
export const mainRouteSteps = (nodes: AnalysisNode[], edges: AnalysisEdge[], splitKey: string, joinKey: string, detourKeys: string[]): string[] => {
    const keys = new Set(nodes.map(n => n._key))
    const inScope = edges.filter(e => keys.has(e.from) && keys.has(e.to))
    const forward = new Map<string, string[]>(), backward = new Map<string, string[]>()
    inScope.forEach(e => {
        ;(forward.get(e.from) || forward.set(e.from, []).get(e.from)!).push(e.to)
        ;(backward.get(e.to) || backward.set(e.to, []).get(e.to)!).push(e.from)
    })
    const reach = (starts: string[], next: Map<string, string[]>, stop: string) => {
        const seen = new Set<string>(starts)
        const queue = [...starts]
        while (queue.length) {
            const k = queue.shift()!
            if (k === stop) continue
            ;(next.get(k) || []).forEach(t => { if (!seen.has(t)) { seen.add(t); queue.push(t) } })
        }
        return seen
    }
    const starts = keys.has(splitKey) ? [splitKey] : edges.filter(e => e.informationKey === splitKey && keys.has(e.to)).map(e => e.to)
    const fromSplit = reach(starts, forward, joinKey)
    const toJoin = reach([joinKey], backward, splitKey)
    const detour = new Set(detourKeys)
    return [...fromSplit].filter(k => toJoin.has(k) && !detour.has(k) && k !== splitKey && k !== joinKey)
}

/**
 * How much of each step's work comes from each input: items flow from the inputs along the connections in the share
 * of items each carries, so a step fed by two inputs is attributed to both in proportion. Worked out by passing the
 * shares along until they settle, which handles paths that split, join and loop
 */
export const attributeToInputs = (nodes: AnalysisNode[], edges: AnalysisEdge[]): Map<string, { [inputKey: string]: number }> => {
    const keys = new Set(nodes.map(n => n._key))
    const incoming = new Map<string, AnalysisEdge[]>()
    edges.filter(e => keys.has(e.from) && keys.has(e.to) && e.from !== e.to).forEach(e => (incoming.get(e.to) || incoming.set(e.to, []).get(e.to)!).push(e))
    const weight = (e: AnalysisEdge) => (e.items > 0 ? e.items : e.percentage > 0 ? e.percentage : 1)

    const shares = new Map<string, { [inputKey: string]: number }>()
    const sources = nodes.filter(isSource)
    sources.forEach(s => shares.set(s._key, { [s._key]: 1 }))
    const others = nodes.filter(n => !isSource(n))

    for (let pass = 0; pass < Math.max(1, nodes.length); pass++) {
        let moved = 0
        others.forEach(n => {
            const into = incoming.get(n._key) || []
            const total = into.reduce((sum, e) => sum + weight(e), 0)
            if (!total) return
            const next: { [inputKey: string]: number } = {}
            into.forEach(e => {
                const from = shares.get(e.from)
                if (!from) return
                Object.keys(from).forEach(k => next[k] = (next[k] || 0) + from[k]*weight(e)/total)
            })
            const before = shares.get(n._key) || {}
            const change = Object.keys({ ...before, ...next }).reduce((m, k) => Math.max(m, Math.abs((next[k] || 0) - (before[k] || 0))), 0)
            if (change > 1e-6) {
                shares.set(n._key, next)
                moved++
            }
        })
        if (!moved) break
    }
    return shares
}

export interface WhatIfData {
    data: { nodes: AnalysisNode[], edges: AnalysisEdge[] }
    roles: Map<string, RoleCapacity>            // as the simulation's report reads them, adjusted
    factors: Map<string, number>                // each step's load as a multiple of today's
    cost: number                                // the organisation's annual cost of people and systems, adjusted
}

/**
 * The analysis data with the what-if applied: the inputs' items changed, every step's items and hours scaled by the
 * share of its work that comes from each input, and the capacity of the steps a role performs scaled by its people.
 * The roles' work and capacity are adjusted the same way for the simulation's report
 */
export const applyWhatIf = (data: { nodes: AnalysisNode[], edges: AnalysisEdge[] }, modelKeys: Set<string>, options: WhatIfOptions, settings: WhatIfSettings,
    organisationCost: number): WhatIfData => {
    const nodes = data.nodes.filter(n => modelKeys.has(n.modelKey))
    const edges = data.edges.filter(e => modelKeys.has(e.modelKey))
    const inputs = new Map(options.inputs.map(i => [i.key, i]))
    const roles = new Map(options.roles.map(r => [r.key, r]))
    const shares = attributeToInputs(nodes, edges)

    const factorOf = (n: AnalysisNode): number => {
        const input = inputs.get(n._key)
        if (input) return inputFactor(input, settings)
        const share = shares.get(n._key)
        if (!share) return settings.loadFactor
        let total = 0, factor = 0
        Object.keys(share).forEach(k => {
            const i = inputs.get(k)
            total += share[k]
            factor += share[k]*(i ? inputFactor(i, settings) : settings.loadFactor)
        })
        // work that reaches a step from nowhere the what-if knows follows the overall load
        return total > 0 ? (factor + (1 - Math.min(1, total))*settings.loadFactor) : settings.loadFactor
    }
    const ratioOf = (n: AnalysisNode): number => {
        const ratios = (n.roles || []).map(r => roles.get(r._key)).filter((r): r is WhatIfRole => !!r).map(r => peopleRatio(r, settings))
        return ratios.length ? Math.min(...ratios) : 1
    }

    const factors = new Map<string, number>()
    const adjusted = nodes.map(n => {
        const factor = factorOf(n)
        factors.set(n._key, factor)
        if (n.superType !== NODE_SUPERTYPE.ACTION) return { ...n, items: (n.items || 0)*factor }
        const ratio = ratioOf(n)
        return {
            ...n,
            items: (n.items || 0)*factor,
            hours: (n.hours || 0)*factor,
            capacity: (n.capacity || 0)*ratio,
            numPeople: n.numPeople > 0 ? Math.max(1, Math.round(n.numPeople*ratio)) : 0,
        }
    })
    const hours = new Map(nodes.filter(n => n.superType === NODE_SUPERTYPE.ACTION).map(n => [n._key, n.hours || 0]))

    // a detour's share changed: its steps' items and hours scale with the share, and the items that no longer take
    // it carry on along the main flow, so the steps there between the split and the join get them
    const byKey = new Map(adjusted.map(n => [n._key, n]))
    options.detours.forEach(d => {
        const share = detourShare(d, settings)
        if (share === d.share || d.share <= 0) return
        const ratio = share/d.share
        d.stepKeys.forEach(k => {
            const n = byKey.get(k)
            if (!n) return
            n.items = (n.items || 0)*ratio
            n.hours = (n.hours || 0)*ratio
            factors.set(k, (factors.get(k) || 1)*ratio)
        })
        // the change in items a year at the split, at the what-if's load
        const splitFactor = factors.get(d.key.split('>')[0]) ?? settings.loadFactor
        const moved = (d.share - share)*d.splitItems*splitFactor
        mainRouteSteps(nodes, edges, d.key.split('>')[0], d.key.split('>')[1], d.stepKeys).forEach(k => {
            const n = byKey.get(k)
            const today = nodes.find(x => x._key === k)
            if (!n || !today) return
            const perItem = today.items > 0 ? (today.hours || 0)/today.items : 0
            n.items = Math.max(0, (n.items || 0) + moved)
            n.hours = Math.max(0, (n.hours || 0) + moved*perItem)
            if (today.hours > 0) factors.set(k, n.hours/today.hours)
        })
    })

    let cost = organisationCost
    const adjustedRoles = new Map<string, RoleCapacity>()
    options.roles.forEach(r => {
        const ratio = peopleRatio(r, settings)
        // the role's work changes by the load of the steps it performs, weighted by their hours
        const work = r.actionKeys.reduce((sum, k) => sum + (hours.get(k) || 0), 0)
        const loadFactor = work > 0 ? r.actionKeys.reduce((sum, k) => sum + (hours.get(k) || 0)*(factors.get(k) || settings.loadFactor), 0)/work : settings.loadFactor
        adjustedRoles.set(r.key, { name: r.name, hours: r.hours*loadFactor, capacity: r.capacity*ratio, people: r.people > 0 ? Math.max(0, settings.people[r.key] ?? r.people) : r.people })
        cost += r.cost*(ratio - 1)
    })

    return { data: { nodes: adjusted, edges }, roles: adjustedRoles, factors, cost }
}

// the people each role needs for the adjusted load, at the capacity each person has today
export const roleRequirements = (options: WhatIfOptions, adjusted: WhatIfData): RoleRequirement[] =>
    options.roles.map(r => {
        const a = adjusted.roles.get(r.key)!
        const perPerson = r.people > 0 && r.capacity > 0 ? r.capacity/r.people : 0
        const needed = perPerson > 0 ? Math.ceil(a.hours/perPerson - 1e-9) : null
        return {
            key: r.key, name: r.name, peopleToday: r.people, people: a.people, hoursToday: r.hours, hours: a.hours, capacity: a.capacity,
            utilisation: a.capacity > 0 ? a.hours/a.capacity : null, needed, extra: needed === null ? 0 : needed - a.people,
            cost: r.cost*(r.people > 0 ? a.people/r.people : 1),
        }
    })

// the settings with every role that can be changed set to what it needs
export const settingsWithNeededPeople = (settings: WhatIfSettings, requirements: RoleRequirement[]): WhatIfSettings => ({
    ...settings,
    people: { ...settings.people, ...Object.fromEntries(requirements.filter(r => r.needed !== null).map(r => [r.key, Math.max(1, r.needed as number)])) },
})

// full-time people, for a number of hours a year
export const fullTimePeople = (hours: number) => Math.round(hours/WORKING_HOURS_PER_YEAR*10)/10
